import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

// One-shot "there's more below" teach: after the swipe preview finishes, a
// tasteful "Scroll for more" pill with a gently bouncing chevron fades in near
// the bottom of the first viewport, holds a moment, then fades out. It never
// hijacks the page or blocks interaction (pointer-events none) and plays once
// per page load. Reduced-motion safe: the pill still appears (static, no
// bounce) so the message reads, then fades. It also hides itself the moment
// the user scrolls (they clearly already know).
//
// Props:
//   delay  — ms before it fades in (defaults past the ~3.2s swipe preview)
//   visibleFor — ms it stays before fading out
export default function ScrollHint({ delay = 3400, visibleFor = 3200 }) {
  const [phase, setPhase] = useState("hidden"); // hidden -> shown -> done
  const [reduced] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );

  useEffect(() => {
    let outT;
    const inT = setTimeout(() => {
      // Already scrolled? Skip — they found the fold on their own.
      if (window.scrollY > 24) {
        setPhase("done");
        return;
      }
      setPhase("shown");
      outT = setTimeout(() => setPhase("done"), visibleFor);
    }, delay);
    return () => {
      clearTimeout(inT);
      clearTimeout(outT);
    };
  }, [delay, visibleFor]);

  // Dismiss on the first user scroll.
  useEffect(() => {
    if (phase === "done") return undefined;
    const onScroll = () => {
      if (window.scrollY > 24) setPhase("done");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [phase]);

  return (
    <AnimatePresence>
      {phase === "shown" && (
        <motion.div
          data-testid="scroll-hint"
          className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom)+0.75rem)] z-30 flex justify-center lg:hidden"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <span className="flex items-center gap-1.5 rounded-full border border-night-line bg-night-surface/90 px-3.5 py-1.5 text-[11px] font-semibold text-night-text shadow-ambient backdrop-blur-sm">
            Scroll for more
            <motion.span
              aria-hidden="true"
              animate={reduced ? undefined : { y: [0, 4, 0] }}
              transition={reduced ? undefined : { duration: 1, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex"
            >
              <ChevronDown size={14} />
            </motion.span>
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
